// MAP FOES (owner 2026-11-02: 'more enemy variety') — render side of the map-only foes. Reads Sim state and SimEvents
// only (rendering never decides gameplay). Three jobs:
//   pose()   per-foe placement tweaks for view.placeArt: hidden underground, wind-up pose, locked facing, leap / dive
//            height, rolling spin, pod swelling
//   event()  the cue bursts ('mapFoe' events: digs, eruptions, gusts, bursts, landings, ignitions …)
//   update() cheap ambient particles: tunnelling dirt, buried-sand ripples, spore motes, frost glints, lava bubbles,
//            the hero's own status (rooted vines, burning, jammed static)
// Ground decals (attack marks, patches, Hush Beacon auras) are drawn by hazardView.ts; colour-blind shape cues by
// telegraphs.ts. All particles go through the shared pools in fx.ts (no new draw calls).
import type { Sim, Enemy, SimEvent } from '../core/sim';
import { DELVER, HOPPER, JAMMER, SKYRAY } from '../core/mapFoeDefs';
import { FZ, type MapFoeEvent } from '../core/mapFoes';
import type { Fx } from './fx';
import type { EnemyArt } from './enemyArt/kit';

/** shift = extra offset (m) along the foe's facing (keeps a rolling ball turning about its own centre) */
export interface FoePose { hide: boolean; wind: number; lockYaw: boolean; rise: number; pitch: number; roll: number; scale: number; shift: number }

const DIRT = 0x8a6a3a, DIRT_L = 0xc9a46a, SPORE = 0xc8ff4a, SPORE_D = 0x7a3fa0, ICE = 0xd8f4ff, LAVA = 0xff7a2a, EMBER = 0xffb040;
/** height (m) of the Rust Roller's body centre: its roll spins about this point */
const ROLL_CENTRE = 0.75;
const WIND = 0xeef8ff, SIGNAL = 0x6ff2ff, SAND = 0xd8b880, LEAF = 0x4aa04a, RUST = 0xc8823a;

export class MapFoeFx {
  private out: FoePose = { hide: false, wind: 0, lockYaw: false, rise: 0, pitch: 0, roll: 0, scale: 1, shift: 0 };
  private acc = 0;
  constructor(private fx: Fx, private groundY: (x: number, z: number) => number) {}

  /** placement tweaks for a map foe this frame (view.placeArt) */
  pose(e: Enemy, sim: Sim, art: EnemyArt, t: number): FoePose {
    const o = this.out; o.hide = e.mode < 0; o.wind = sim.foes.windK(e); o.lockYaw = false; o.rise = 0; o.pitch = 0; o.roll = 0; o.scale = 1; o.shift = 0;
    if (o.hide) return o;
    const pace = sim.pace, hover = art.hover ?? 0;
    switch (e.def.kind) {
      case 'delver':
        if (e.mode === 1) { const k = 1 - Math.max(0, e.modeT) * pace / DELVER.dig; o.rise = -1.1 * k * k; o.pitch = 0.35 * k; }
        break;
      case 'roller':
        if (e.mode === 1 || e.mode === 2) { o.lockYaw = true; o.wind = 1; }
        if (e.mode === 2) { const a = e.phase * 13, h = ROLL_CENTRE * e.scale; o.pitch = a; o.rise = h * (1 - Math.cos(a)); o.shift = -h * Math.sin(a); } // a ball spinning about its own centre
        if (e.mode === 3) o.roll = Math.sin(t * 7 + e.id) * 0.22; // dizzy wobble
        break;
      case 'sporepod':
        if (e.mode === 1) { const m = sim.foes.markFor(e), k = m ? Math.min(1, m.t / m.warn) : 1; o.scale = 1 + 0.3 * k + Math.sin(t * 30) * 0.03 * k; }
        else o.scale = 1 + Math.sin(e.phase * 2.2) * 0.04; // breathing
        break;
      case 'lasher': case 'lurker': case 'kite':
        if (e.mode === 1 || e.mode === 2) o.lockYaw = true;
        if (e.def.kind === 'lurker' && e.mode === 2) o.pitch = 0.25;
        break;
      case 'hopper':
        if (e.mode === 1 || e.mode === 2) o.lockYaw = true;
        if (e.mode === 2) { const k = sim.foes.airK(e); o.rise = Math.sin(Math.PI * k) * 3.2; o.pitch = -0.4 + 0.8 * k; o.wind = 0; }
        break;
      case 'skyray':
        if (e.mode === 1) { const m = sim.foes.markFor(e), k = m ? Math.min(1, m.t / m.warn) : 1; o.rise = 1.4 * k; o.pitch = -0.2 * k; }
        else if (e.mode === 2) { const k = sim.foes.airK(e); o.rise = 1.4 * (1 - k) - hover * 0.85 * k * k; o.pitch = 0.5; }
        else if (e.mode === 3) o.rise = -hover * 0.85;
        break;
    }
    return o;
  }

  event(ev: SimEvent, sim: Sim): void {
    if (ev.t !== 'mapFoe') return;
    this.cue(ev, sim);
  }

  /** earthy dust (alpha smoke: the glow particle pool uses max blending, so dark dirt would vanish on light ground) */
  private dust(x: number, z: number, n: number, r: number, sp: number, life: number, s1: number, tone = 0): void {
    const B = this.fx.blast; if (!B) return;
    const gy = this.groundY(x, z);
    for (let k = 0; k < n; k++) { const a = Math.random() * 6.283, d = Math.random() * r; B.dust(x + Math.cos(a) * d, gy + 0.2, z + Math.sin(a) * d, Math.cos(a) * sp * (0.4 + Math.random() * 0.6), 0.6 + Math.random() * sp * 0.5, Math.sin(a) * sp * (0.4 + Math.random() * 0.6), life * (0.7 + Math.random() * 0.5), 0.5, s1, tone); }
  }
  private stones(x: number, z: number, n: number, up: number, spread: number, size: number, col: number): void {
    this.fx.blast?.pebbles(x, this.groundY(x, z) + 0.3, z, n, 0, up, 0, spread, size, col, 1.4);
  }

  private cue(ev: MapFoeEvent, sim: Sim): void {
    const fx = this.fx, gy = this.groundY(ev.x, ev.z), P = fx.particles;
    const ring = (r: number, col: number, life = 0.5, k = 1.4) => fx.rings.add(ev.x, gy + 0.12, ev.z, r, life, col, k, 1);
    const puff = (n: number, sp: number, col: number, size: number, life: number, up = 2) => P.emit(ev.x, gy + 0.3, ev.z, n, sp, col, size, life, up, -3, 2, 1.6);
    const along = (len: number, col: number, n: number, sp: number) => { for (let k = 0; k < n; k++) { const d = Math.random() * len; P.emitDir(ev.x + ev.dx * d + (Math.random() - 0.5), gy + 0.3 + Math.random() * 0.8, ev.z + ev.dz * d + (Math.random() - 0.5), ev.dx * sp, 0.4, ev.dz * sp, col, 0.4, 0.45, 2); } };
    switch (ev.k) {
      case 'dig': this.dust(ev.x, ev.z, 8, 0.8, 2.5, 0.9, 1.6, 0.3); this.stones(ev.x, ev.z, 8, 4, 2, 0.12, DIRT); break;
      case 'sprout': if (ev.kind === 'sporepod') puff(10, 3, LEAF, 0.45, 0.7, 2.5); this.dust(ev.x, ev.z, 5, 0.6, 1.5, 0.8, 1.2, ev.kind === 'sporepod' ? 0.4 : 0); break;
      case 'erupt':
        this.dust(ev.x, ev.z, 16, ev.r * 0.8, 6, 1.3, 2.6, 0.35); this.stones(ev.x, ev.z, 18, 9, 4, 0.18, DIRT);
        puff(10, 5, DIRT_L, 0.6, 0.6, 5); ring(ev.r * 1.6, DIRT_L, 0.6, 1.2);
        fx.shake = Math.min(1, fx.shake + 0.18);
        break;
      case 'mark':
        if (ev.kind === 'delver') { this.dust(ev.x, ev.z, 6, ev.r * 0.7, 1, 1.2, 1.4, 0.3); this.stones(ev.x, ev.z, 6, 3, 1.5, 0.1, DIRT); }
        else if (ev.kind === 'skyray') ring(ev.r * 1.3, WIND, 0.5, 0.8);
        break;
      case 'curl': if (ev.kind === 'roller') puff(8, 2.5, RUST, 0.45, 0.5, 1.5); else puff(6, 2, DIRT, 0.4, 0.5, 1.5); break;
      case 'bounce': puff(12, 6, EMBER, 0.3, 0.35, 2); this.dust(ev.x, ev.z, 6, 0.8, 3, 0.8, 1.8, 0.2); this.stones(ev.x, ev.z, 6, 4, 3, 0.12, RUST); ring(1.8, RUST, 0.35, 1.2); fx.shake = Math.min(1, fx.shake + 0.08); break;
      case 'swell': break; // the decal + the swelling body carry it
      case 'burst': puff(34, 4.5, SPORE, 0.9, 1.4, 1.5); puff(14, 3, SPORE_D, 0.8, 1.2, 1); ring(ev.r * 1.2, SPORE, 0.6, 1.1); break;
      case 'wither': this.dust(ev.x, ev.z, 4, 0.5, 1, 1, 1.2, 0.5); break;
      case 'lashWind': puff(6, 1.5, ev.kind === 'lurker' ? SAND : LEAF, 0.35, 0.5, 1); break;
      case 'lash': along(ev.r, LEAF, 18, 6); fx.shake = Math.min(1, fx.shake + 0.05); break;
      case 'root': { const p = sim.player, hy = this.groundY(p.x, p.z); for (let k = 0; k < 16; k++) { const a = (k / 16) * 6.283; P.emitDir(p.x + Math.cos(a) * 0.6, hy + 0.1, p.z + Math.sin(a) * 0.6, -Math.cos(a) * 0.5, 2.4, -Math.sin(a) * 0.5, LEAF, 0.35, 0.7, 2); } break; }
      case 'crouch': puff(6, 1.5, LAVA, 0.3, 0.5, 1); break;
      case 'leap': this.dust(ev.x, ev.z, 5, 0.6, 2, 0.7, 1.3, 1); break;
      case 'land':
        if (ev.kind === 'hopper') { puff(26, 7, LAVA, 0.55, 0.8, 5); this.dust(ev.x, ev.z, 8, 1.2, 4, 1, 2, 1); this.stones(ev.x, ev.z, 10, 6, 3, 0.15, 0x45434c); ring(HOPPER.r * 1.5, LAVA, 0.5, 1.5); }
        else { puff(28, 7, WIND, 0.6, 0.7, 3); this.dust(ev.x, ev.z, 10, 1.5, 5, 1, 2.4, 0); ring(SKYRAY.r * 1.6, WIND, 0.5, 1.2); }
        fx.shake = Math.min(1, fx.shake + 0.14);
        break;
      case 'ignite': puff(14, 4, EMBER, 0.35, 0.5, 3); break;
      case 'gustWind': puff(6, 1, WIND, 0.4, 0.6, 0.5); break;
      case 'gust': along(ev.r, WIND, 30, 14); break;
      case 'dive': for (let k = 0; k < 8; k++) P.emitDir(ev.x, gy + 3, ev.z, (Math.random() - 0.5) * 2, 1, (Math.random() - 0.5) * 2, WIND, 0.3, 0.6, 2); break;
      case 'ambush': this.dust(ev.x, ev.z, 10, 1, 3, 1.1, 1.8, 0); this.stones(ev.x, ev.z, 8, 4, 2, 0.1, SAND); ring(1.8, SAND, 0.5, 1); break;
      case 'lunge': this.dust(ev.x, ev.z, 12, 1, 4, 1, 2, 0); this.stones(ev.x, ev.z, 10, 5, 3, 0.12, SAND); break;
      case 'jam': { const p = sim.player; fx.rings.add(p.x, this.groundY(p.x, p.z) + 0.15, p.z, JAMMER.r, 0.5, SIGNAL, 1.2, 1); break; }
    }
  }

  /** ambient per-frame particles (dt = render seconds) */
  update(sim: Sim, dt: number, t: number): void {
    if (!sim.foes.active) return;
    this.acc += dt;
    const P = this.fx.particles, rate = (n: number) => Math.random() < n * dt;
    for (const e of sim.enemies) {
      if (!e.alive || !e.def.map) continue;
      const gy = this.groundY(e.x, e.z);
      switch (e.def.kind) {
        case 'delver':
          if (e.mode < 0 && rate(e.mode === -2 ? 30 : 16)) { // tunnelling: a churned-dirt trail; the eruption warning makes it boil
            this.fx.blast?.dust(e.x + (Math.random() - 0.5) * 1.2, gy + 0.15, e.z + (Math.random() - 0.5) * 1.2, (Math.random() - 0.5), 0.8 + Math.random(), (Math.random() - 0.5), 0.7, 0.35, 1.1, 0.35);
            if (rate(8)) this.stones(e.x, e.z, 1, 3, 1, 0.1, DIRT);
          }
          break;
        case 'lurker':
          if (e.mode === -1 && rate(2.5)) { const a = Math.random() * 6.283; this.fx.blast?.dust(e.x + Math.cos(a) * 0.8, gy + 0.08, e.z + Math.sin(a) * 0.8, Math.cos(a) * 0.5, 0.3, Math.sin(a) * 0.5, 0.9, 0.3, 0.9, 0); } // a faint ripple in the sand
          if (e.mode === -2 && rate(30)) this.fx.blast?.dust(e.x + (Math.random() - 0.5) * 1.4, gy + 0.1, e.z + (Math.random() - 0.5) * 1.4, 0, 2, 0, 0.5, 0.3, 1, 0);
          break;
        case 'roller': if (e.mode === 2 && rate(24)) this.fx.blast?.dust(e.x - e.dirX * 0.8, gy + 0.2, e.z - e.dirZ * 0.8, -e.dirX * 2, 0.8, -e.dirZ * 2, 0.7, 0.4, 1.3, 0.15); break;
        case 'wraith': if (rate(10)) P.emitDir(e.x, gy + 0.8 + Math.random() * 0.8, e.z, (Math.random() - 0.5), -0.6, (Math.random() - 0.5), ICE, 0.3, 0.8, 1.8); break;
        case 'mite': if (rate(6)) P.emitDir(e.x, gy + 0.3, e.z, 0, 1.2, 0, EMBER, 0.18, 0.35, 2.5); break;
        case 'jammer': if (e.edgeT < 0 && rate(4)) { const a = Math.random() * 6.283; P.emitDir(e.x + Math.cos(a) * JAMMER.r, gy + 0.2, e.z + Math.sin(a) * JAMMER.r, 0, 1.4, 0, SIGNAL, 0.3, 0.5, 2.2); } break;
        case 'sporepod': if (rate(1.5)) P.emitDir(e.x, gy + 1.1, e.z, (Math.random() - 0.5) * 0.4, 0.5, (Math.random() - 0.5) * 0.4, SPORE, 0.22, 1.2, 1.6); break;
      }
    }
    for (const z of sim.foes.zones) {
      if (z.t < z.form) continue;
      const fade = Math.min(1, (z.life - z.t) / 0.8), gy = this.groundY(z.x, z.z);
      const a = Math.random() * 6.283, d = Math.sqrt(Math.random()) * z.r, x = z.x + Math.cos(a) * d, zz = z.z + Math.sin(a) * d;
      if (z.kind === FZ.spore && rate(14 * fade)) P.emitDir(x, gy + 0.3 + Math.random(), zz, (Math.random() - 0.5) * 0.4, 0.3, (Math.random() - 0.5) * 0.4, Math.random() < 0.7 ? SPORE : SPORE_D, 0.5, 1.2, 1.3);
      else if (z.kind === FZ.ice && rate(4 * fade)) P.emitDir(x, gy + 0.08, zz, 0, 0.2, 0, ICE, 0.22, 0.5, 2.4);
      else if (z.kind === FZ.lava && rate(10 * fade)) P.emitDir(x, gy + 0.1, zz, 0, 1.6, 0, Math.random() < 0.6 ? LAVA : EMBER, 0.3, 0.45, 2.4);
    }
    // the hero's own status
    const p = sim.player, hy = this.groundY(p.x, p.z), F = sim.foes;
    if (F.rootT > 0 && rate(30)) { const a = Math.random() * 6.283; P.emitDir(p.x + Math.cos(a) * 0.5, hy + 0.1, p.z + Math.sin(a) * 0.5, 0, 1.6, 0, LEAF, 0.3, 0.4, 2); }
    if (F.burnT > 0 && rate(26)) P.emitDir(p.x + (Math.random() - 0.5) * 0.7, hy + 0.5 + Math.random() * 1.4, p.z + (Math.random() - 0.5) * 0.7, 0, 2.2, 0, Math.random() < 0.5 ? LAVA : EMBER, 0.3, 0.4, 2.4);
    if (F.cdRate < 1 && rate(14)) { const a = Math.random() * 6.283; P.emitDir(p.x + Math.cos(a) * 0.7, hy + 0.6 + Math.random() * 1.2, p.z + Math.sin(a) * 0.7, -Math.sin(a) * 2, 0, Math.cos(a) * 2, SIGNAL, 0.2, 0.25, 2.6); }
    if (F.pushT > 0 && rate(40)) P.emitDir(p.x + (Math.random() - 0.5), hy + 0.4 + Math.random(), p.z + (Math.random() - 0.5), 0, 0.2, 0, WIND, 0.35, 0.3, 2);
    void t;
  }
}
